import { Dispatch, SetStateAction, useState } from "react";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { sendEmailPatientFormSchema } from "../../schema/sendEmailPatientFormSchema";
import AppHtmlEditor from "../../../../components/AppHtmlEditor/AppHtmlEditor";
import { ClaimFileDto, ClaimPatientEmailForm } from "../../types";
import { sendEmailToPatient } from "../../api/ClaimAPI";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import AlertMessage from "../../../../components/AlertMessage";
import { useAlert } from "../../../../hooks/useAlert";
import AppDropdownButton, { AppDropdownButtonOption } from "../../../../components/AppDropdownButton/AppDropdownButton";
import { useCurrentClient } from "../../../auth/api/getCurrentClient";
import ModalGlobal from "../../../../components/AppModal/ModalGlobal";

interface SendEmailToPatientPageProps {
    claimId: number;
    claimantEmailAddress?: string;
    affectedPersonEmailAddress?: string;
    suggestedMessage?: string;
    setShowSendEmailPage: Dispatch<SetStateAction<boolean>>;
    onEmailSendedSuccessfully: (claimFile: ClaimFileDto) => void
}

export default function SendEmailToPatientPage({
    claimId,
    claimantEmailAddress,
    affectedPersonEmailAddress,
    suggestedMessage,
    setShowSendEmailPage,
    onEmailSendedSuccessfully
} : SendEmailToPatientPageProps) {
    const { data: configClient } = useCurrentClient();
    const { t } = useTranslation(['claim']);

    const [showConfirmCopyModal, setShowConfirmCopyModal] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [formData, setFormData] = useState<ClaimPatientEmailForm>({
        body: "",
        communicationType: 0,
        subject: configClient?.claimPatientEmailSubject ?? "",
        destinationAddressEmail: "",
        claimId
    });
    const [errors, setErrors] = useState<{ [key: string]: string | undefined }>({});

    const { showAlert, hideAlert, alert, isVisible: showAlertMessage } = useAlert({ fromLocation: false });
    
    const onSendEmail = async () => {
        setIsLoading(true);

        const schema = sendEmailPatientFormSchema(t);

        try {
            await schema.validate(formData, { abortEarly: false });
            setErrors({});
        } catch (validationError: any) {
            const errors: Record<string, string | undefined> = {};
            validationError.inner?.forEach((item: any) => {
                if (item.path) {
                    errors[item.path] = item.message;
                }
            });
            setErrors(errors);
            return;
        }

        sendEmailToPatient(formData)
            .then((data) => {
                setShowSendEmailPage(false);
                onEmailSendedSuccessfully(data);
            })
            .catch((error: ApiErrorResponse) => {
                showAlert({
                    message: error.message ? t(error.message) : t("common:errors.networkError"),
                    type: "danger",
                    duration: 3000,
                });
            }).finally(() => {
                setIsLoading(false);
            })
    }

    const copySuggestedMessage = () => {
        if (formData.body) {
            setShowConfirmCopyModal(true);
        } else {
            confirmCopySuggestedMessage();
        }
    }

    const confirmCopySuggestedMessage = () => {
        const textToHTML = (suggestedMessage ?? "")
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/\r\n|\r|\n/g, "<br>");

        setFormData((prev) => ({
            ...prev,
            body: textToHTML
        }));
    }

    const personButtonOptions: AppDropdownButtonOption[] = [
        {
            label: `+ ${t("claim:sendEmailPatient.affected")}`,
            onClick: () => {
                setFormData(prev => ({
                    ...prev,
                    destinationAddressEmail: prev.destinationAddressEmail.trim().length > 0 
                        ? prev.destinationAddressEmail.concat(`; ${affectedPersonEmailAddress}`)
                        : (affectedPersonEmailAddress ?? "")
                }));
            },
        },
        {
            label: `+ ${t("claim:sendEmailPatient.claimant")}`,
            onClick: () => {
                setFormData(prev => ({
                    ...prev,
                    destinationAddressEmail: prev.destinationAddressEmail.trim().length > 0 
                        ? prev.destinationAddressEmail.concat(`; ${claimantEmailAddress}`)
                        : (claimantEmailAddress ?? "")
                }));
            },
        }
    ];

    return(
        <Form>
            {isLoading && (
                <div className="overlay-spinner">
                    <Spinner animation="border" variant="light" />
                </div>
            )}
            <ModalGlobal
                show={showConfirmCopyModal}
                onClose={() => setShowConfirmCopyModal(false)}
                body={t("claim:sendEmailPatient.confirmCopySuggestedMessageText")}
                saveText={t("common:modalActionOK")}
                closeText={t("common:modalActionNOTOK")}
                onSave={() => {
                    confirmCopySuggestedMessage();
                    setShowConfirmCopyModal(false);
                }}
                isLoading={false}
            />
            <Row className="mt-4">
                <Col className="d-flex justify-content-end gap-2">
                    <Button variant="secondary" onClick={() => setShowSendEmailPage(false)}>
                        {t("common:buttons.buttonCancel")}
                    </Button>
                    <Button variant="primary" onClick={onSendEmail}>
                        {t("common:buttons.buttonSend")}
                    </Button>
                </Col>
            </Row>
                
            <Row>
                {showAlertMessage && (
                    <AlertMessage
                        message={alert!.message}
                        type={alert!.type}
                        onClose={hideAlert}
                    />
                )}
            </Row>
            
            <Row>
                <Col xs={12} md={12}>
                    <Form.Label>{t("claim:sendEmailPatient.communicationType")}</Form.Label>
                    <Form.Select
                        className="form-control"
                        value={formData.communicationType}
                        onChange = {(e) => {
                            const selectedId = e.target.value === "" ? 0 : Number(e.target.value);
                            setFormData((prev) => { 
                                return {
                                    ...prev,
                                    communicationType: selectedId
                                }});
                        }}
                    >
                        <option value=""></option>
                    </Form.Select>
                </Col>
            </Row>
            <Row>
                <Col md={3}>
                    <Form.Group>
                        <Form.Label>{t("claim:sendEmailPatient.to")}</Form.Label>
                        <Form.Control
                            type="text"
                            value={formData.destinationAddressEmail}
                            isInvalid={!!errors.destinationAddressEmail}
                            onChange = {(e) => setFormData((prev) => {
                                return {
                                    ...prev,
                                    destinationAddressEmail: e.target.value
                                }
                            })}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.destinationAddressEmail}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col md={1} className="d-flex align-items-end">
                    <AppDropdownButton
                        label={`+ ${t("claim:sendEmailPatient.person")}`}
                        options={personButtonOptions}
                        buttonClassName="btn-principal"
                    />
                </Col>
                <Col md={4}>
                    <Form.Group>
                        <Form.Label>{t("claim:sendEmailPatient.cc")}</Form.Label>
                        <Form.Control
                            type="text"
                            value={formData.cc}
                            isInvalid={!!errors.cc}
                            onChange = {(e) => setFormData((prev) => {
                                return {
                                    ...prev,
                                    cc: e.target.value
                                }
                            })}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.cc}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group>
                        <Form.Label>{t("claim:sendEmailPatient.bcc")}</Form.Label>
                        <Form.Control
                            type="text"
                            value={formData.bcc}
                            isInvalid={!!errors.bcc}
                            onChange = {(e) => setFormData((prev) => {
                                return {
                                    ...prev,
                                    bcc: e.target.value
                                }
                            })}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.bcc}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col md={12}>
                    <Form.Group>
                        <Form.Label>{t("claim:detail.subject")}</Form.Label>
                        <Form.Control
                            type="text"
                            value={formData.subject}
                            isInvalid={!!errors.subject}
                            onChange = {(e) => setFormData((prev) => {
                                return {
                                    ...prev,
                                    subject: e.target.value
                                }
                            })}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.subject}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>
            <Row className="mt-3">
                {suggestedMessage &&
                    <Col md={12} className="d-flex justify-content-end mb-1">
                        <Button type="button" variant="secondary" onClick={copySuggestedMessage}>
                            {t("claim:button.copySuggestedAI")}
                        </Button>
                    </Col>
                }
                <Col md={12}>
                    <Form.Group>
                        <AppHtmlEditor
                            value={formData.body}
                            isInvalid={!!errors.body}
                            onChange={(content: string) => setFormData((prev) => {
                                return {
                                    ...prev,
                                    body: content
                                }
                            })}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.body}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>
        </Form>
    );
}